import { ref, reactive } from 'vue';
import { useToast } from 'vue-toastification';
import apiClient, { getErrorMessage } from '@/api/apiClient';
import { t, entityParams } from '@/i18n';

// State and actions shared by the Setup lists (categories, subcategories, bills, users):
// the list itself, add/edit modal state and delete with confirmation.
//
//   entity:     key of the thing in the translations ("recurringBill"), used to build the messages
//   listUrl:    endpoint that returns the list
//   deleteUrl:  endpoint that deletes one item
//   deleteKey:  key the item is sent under in the delete request body
//   track:      wraps a request promise (see useLoading)
//   onDeleted:  called after a successful delete
export function useCrudList({ entity, listUrl, deleteUrl, deleteKey, track, onDeleted }) {
    const toast = useToast();
    const items = ref([]);
    const isAddOpen = ref(false);
    const isEditOpen = ref(false);
    const selected = ref(null);

    const fetchItems = () => {
        return track(
            apiClient.post(listUrl)
                .then((response) => {
                    items.value = response.data;
                })
                .catch((error) => {
                    console.error(`Error fetching ${entity} list:`, error);
                })
        );
    };

    const openAdd = () => {
        isAddOpen.value = true;
    };

    const openEdit = (item) => {
        selected.value = item;
        isEditOpen.value = true;
    };

    const remove = (item) => {
        if (!window.confirm(t('crud.confirmDelete', entityParams(entity)))) {
            return;
        }

        apiClient.post(deleteUrl, { [deleteKey]: item })
            .then((response) => {
                if (response.data.success === true) {
                    onDeleted();
                    toast.success(t('crud.deleted', entityParams(entity)));
                } else {
                    console.error(`Failed to delete ${entity}.`);
                    toast.error(response.data.message || t('crud.failedDelete', entityParams(entity)));
                }
            })
            .catch((error) => {
                console.error(`Error deleting ${entity}:`, error);
                toast.error(t('crud.errorDeleting', { ...entityParams(entity), detail: getErrorMessage(error) }));
            });
    };

    // reactive() unwraps the refs so templates can use `categories.isAddOpen` directly
    return reactive({ items, isAddOpen, isEditOpen, selected, fetchItems, openAdd, openEdit, remove });
}
